import React, { useState } from 'react';
import { usePostHog } from '@posthog/react';
import { enableHubSpotTracking } from '../analytics/hubspot';

const CONSENT_KEY = 'lanura_cookie_consent';

/**
 * Minimal PECR/ePrivacy consent banner.
 *
 * Before a choice is made, PostHog runs cookieless (memory persistence, no
 * session recording; configured in index.tsx). Accept upgrades analytics to
 * persistent storage and enables session replay; Decline keeps the cookieless
 * mode permanently. Storing the choice itself is strictly necessary and
 * therefore exempt. Accept and Decline have equal visual prominence, per
 * ICO guidance.
 *
 * HubSpot follows the same rule. Its loader is told doNotTrack before it runs
 * and is only released here, so the one Accept covers both.
 */
const CookieConsent: React.FC = () => {
  const ph = usePostHog();
  const [choice, setChoice] = useState<string | null>(() => {
    // If storage is blocked we can't remember an answer, so hide the banner
    // and stay in the cookieless default (declined) rather than nag forever.
    try {
      return localStorage.getItem(CONSENT_KEY);
    } catch {
      return 'declined';
    }
  });

  if (choice) return null;

  const decide = (accepted: boolean) => {
    try {
      localStorage.setItem(CONSENT_KEY, accepted ? 'accepted' : 'declined');
    } catch {}
    if (!accepted) {
      // Declined means no analytics events at all, not quieter ones (R20).
      try {
        ph?.opt_out_capturing();
      } catch {}
    }
    if (accepted) {
      try {
        ph?.set_config({ persistence: 'localStorage+cookie' } as any);
        ph?.startSessionRecording?.();
      } catch {}
      /* HubSpot was loaded with doNotTrack pushed, so it has been running
       * without cookies (analytics/hubspot.ts). Accept is the moment it is
       * allowed to identify this visitor. A no-op on any host where it never
       * loaded. */
      try {
        enableHubSpotTracking();
      } catch {}
    }
    setChoice(accepted ? 'accepted' : 'declined');
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 560,
        margin: '0 auto',
        background: 'rgba(255,255,255,0.98)',
        color: '#0f172a',
        border: '1px solid rgba(15,23,42,0.12)',
        borderRadius: 14,
        boxShadow: '0 12px 32px rgba(15,23,42,0.16)',
        padding: '14px 16px',
        fontSize: 13.5,
        lineHeight: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span style={{ flex: '1 1 260px', minWidth: 220 }}>
        We use optional analytics cookies to understand how Lanura is used and improve it. Essential
        cookies for sign-in always stay on.{' '}
        <a href="/privacy" style={{ color: '#0f8b8e', fontWeight: 600 }}>
          Privacy policy
        </a>
      </span>
      <span style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
        <button
          type="button"
          onClick={() => decide(false)}
          style={{
            appearance: 'none',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
            padding: '8px 14px',
            borderRadius: 999,
            background: '#fff',
            color: '#0f172a',
            border: '1px solid rgba(15,23,42,0.25)',
          }}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={() => decide(true)}
          style={{
            appearance: 'none',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
            padding: '8px 14px',
            borderRadius: 999,
            /* #0f8b8e measured 4.11:1 against white, under the 4.5:1 AA
               floor for 13px text, and PageSpeed flagged this button by name.
               #0c7679 is 5.41:1 and is the same --lp-teal-text already used
               for teal wording on the landing page. */
            background: '#0c7679',
            color: '#fff',
            border: '1px solid #0f8b8e',
          }}
        >
          Accept
        </button>
      </span>
    </div>
  );
};

export default CookieConsent;
